/**
 * flat-library — slug → public-icon-path manifest for the multicolor
 * illustrated icon set in `apps/ROP-teams/public/icons/`.
 *
 * Bill 2026-05-13 — comprehensive icon mapping across:
 *   - Product Categories (6)
 *   - Services (5 canonical)
 *   - Special Purpose subtype gap-fillers (2 — MedicalSetAside, USDAGrant)
 *   - Cart-Mode Badges (3 visible; Production renders no icon by design)
 *   - Card Designs (4 PCB-branded debit cards, per Collette 2026-05-08)
 *   - Hero Domain Rails (8 — module nav identification)
 *   - Cart Pipeline Phases (7 — for PipelinePreviewInline)
 *   - Additional Document Types (~10 beyond v2.3's core 10)
 *   - Status Tiers (Future badge for roadmap subtypes)
 *
 * These icons coexist with the PCB-bespoke v2.3/v2.5 entity icons in
 * `@/icons/pcb-modal/` (used for cohort picker / Person Add Wizard).
 * Different visual languages serving different surface roles:
 *   - pcb-modal   = clean iconographic, PCB blue palette, cohort identity
 *   - flat-library = multicolor illustrated, broader visual range, products/
 *                    services/modes/admin chrome/pipeline/documents
 *
 * Resolution pattern (use the helpers below or the lookup maps directly):
 *
 *   import { productCategoryIconUrl } from '@/icons/flat-library';
 *   <img src={productCategoryIconUrl('dda')} alt="" width={32} height={32} />
 */

// ─── Product Categories ────────────────────────────────────────────────────
// Used by the Products stage category-picker modal (Screen 22) + Cart card
// product badges.

export const PRODUCT_CATEGORY_ICONS = {
  dda: '/icons/wallet.svg',
  sav: '/icons/savings.svg',
  cd:  '/icons/certificate-deposit.svg',
  ira: '/icons/ira.svg',
  sdb: '/icons/safe-deposit.svg',
  mma: '/icons/money-market.svg',
} as const;

export type ProductCategorySlug = keyof typeof PRODUCT_CATEGORY_ICONS;

export function productCategoryIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase() as ProductCategorySlug;
  return PRODUCT_CATEGORY_ICONS[lower];
}

// ─── Services (canonical 5 per Bill 2026-05-12) ────────────────────────────
// Rendered on AddToCartModal Services section. ODP uses card-locked because
// the OD-protect concept = "protected card."

export const SERVICE_ICONS = {
  'debit-card':           '/icons/debit-card.svg',
  'order-checks':         '/icons/check-payment.svg',
  'e-statements':         '/icons/e-stmts.svg',
  'internet-banking':     '/icons/internet-banking.svg',
  'overdraft-protection': '/icons/card-locked.svg',
} as const;

export type ServiceSlug = keyof typeof SERVICE_ICONS;

// Common variations of the canonical 5 service codes. Lets the resolver
// accept whatever Feature.code shape the seed produces (DEBIT_CARD,
// debit_card, OD_PROTECTION, OVERDRAFT_PROTECTION, IB, etc.).
const SERVICE_CODE_ALIASES: Readonly<Record<string, ServiceSlug>> = {
  'od-protection':         'overdraft-protection',
  od:                      'overdraft-protection',
  overdraft:               'overdraft-protection',
  ib:                      'internet-banking',
  estmt:                   'e-statements',
  estmts:                  'e-statements',
  estatements:             'e-statements',
  checks:                  'order-checks',
  debit:                   'debit-card',
};

export function serviceIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase().replace(/_/g, '-');
  const canonical = (SERVICE_CODE_ALIASES[lower] ?? lower) as ServiceSlug;
  return SERVICE_ICONS[canonical];
}

// ─── Special Purpose subtype gap-fillers ───────────────────────────────────
// The v2.5 PCB-bespoke set is missing MedicalSetAside + USDAGrant. These
// flat-library SVGs fill those slots until/unless a v2.6 PCB-bespoke pair
// is commissioned.

export const SPECIAL_PURPOSE_GAP_ICONS = {
  'medical-set-aside': '/icons/medical.svg',
  'usda-grant':        '/icons/tractor.svg',
} as const;

export type SpecialPurposeGapSlug = keyof typeof SPECIAL_PURPOSE_GAP_ICONS;

export function specialPurposeGapIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase() as SpecialPurposeGapSlug;
  return SPECIAL_PURPOSE_GAP_ICONS[lower];
}

// ─── Cart-Mode Badges ──────────────────────────────────────────────────────
// Bill 2026-05-13 — Production renders NO icon by design spec (default mode,
// no visual emphasis needed). The 3 non-Production modes each get a
// distinctive flat-library icon for the ModeBanner.

export const MODE_ICONS = {
  Production: null,
  'UAT-Live':  '/icons/pilot.svg',
  'Test-Live': '/icons/test-mode2.svg',
  Sandbox:    '/icons/sandcastle.svg',
} as const;

export type ModeSlug = keyof typeof MODE_ICONS;

export function modeIconUrl(mode: string): string | null | undefined {
  const m = mode as ModeSlug;
  return MODE_ICONS[m];
}

// ─── PCB Debit Card Designs ────────────────────────────────────────────────
// Per Collette 2026-05-08 — 4 active debit card designs. PNG card-face
// renders sourced from PCB marketing assets. Keys match
// `CardTypes.Code` in the database (101, 103, 104, 105 per
// `reference_pcb_debit_card_designs_collette_20260508.md`).

export const CARD_DESIGN_ICONS = {
  '101': '/icons/card-designs/101-rt66.png',
  '103': '/icons/card-designs/103-dog.png',
  '104': '/icons/card-designs/104-white-hex.png',
  '105': '/icons/card-designs/105-main-bank-blue.png',
} as const;

export type CardDesignCode = keyof typeof CARD_DESIGN_ICONS;

export function cardDesignIconUrl(code: string): string | undefined {
  return CARD_DESIGN_ICONS[code as CardDesignCode];
}

// ─── Hero Domain Rail Icons ────────────────────────────────────────────────
// Left module rail + admin landing tiles. Identifies each top-level module
// at a glance. Bill 2026-05-13 picks.

export const DOMAIN_RAIL_ICONS = {
  'account-opening':       '/icons/cart-add.svg',
  'records-management':    '/icons/archive.svg',
  'risk-approvals':        '/icons/risk-approval.svg',
  admin:                   '/icons/settings.svg',
  rao:                     '/icons/bank-teller.svg',
  rcm:                     '/icons/maintenance.svg',
  reporting:               '/icons/metrics.svg',
  notifications:           '/icons/notice.svg',
} as const;

export type DomainRailSlug = keyof typeof DOMAIN_RAIL_ICONS;

export function domainRailIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase() as DomainRailSlug;
  return DOMAIN_RAIL_ICONS[lower];
}

// ─── Cart Pipeline Phases ──────────────────────────────────────────────────
// Used by PipelinePreviewInline to visualize the 7-phase commit pipeline per
// project_canonical_cart_pipeline_20260508.md.

export const PIPELINE_PHASE_ICONS = {
  'compliance-preflight':  '/icons/departure-airport-svgrepo-com.svg',
  'account-numbers':       '/icons/tag-svgrepo-com.svg',
  documents:               '/icons/folder.svg',
  'customer-signatures':   '/icons/signature-doc-svgrepo-com.svg',
  'account-creation':      '/icons/card-success.svg',
  'services-cards':        '/icons/card.svg',
  'relationships-audit':   '/icons/relationship.svg',
} as const;

export type PipelinePhaseSlug = keyof typeof PIPELINE_PHASE_ICONS;

export function pipelinePhaseIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase() as PipelinePhaseSlug;
  return PIPELINE_PHASE_ICONS[lower];
}

// ─── Additional Document Types ─────────────────────────────────────────────
// Augments the v2.3 core 10 (PhotoID/SSN/GreenCard/Passport/USVisa/I94/
// I20/DS2019/EAD/AddressProof) with PCB-specific document types referenced
// across the SPEC + procedure-lock memos.
//
// Bill 2026-05-13 picks. Note: court-order and partnership-agreement share
// the same agreement-deal SVG — visually identical "two-party agreement"
// metaphor works for both contexts; the text label disambiguates.

export const DOCUMENT_TYPE_ICONS = {
  'trust-cert':             '/icons/agreement-certification-contract-svgrepo-com.svg',
  'articles-of-incorporation': '/icons/agreement-document-svgrepo-com.svg',
  'banking-resolution':     '/icons/agreement-contract-a4-paper-svgrepo-com.svg',
  'operating-agreement':    '/icons/agreement-svgrepo-com.svg',
  'partnership-agreement':  '/icons/agreement-deal-svgrepo-com.svg',
  'dba-certificate':        '/icons/diploma-contract-svgrepo-com.svg',
  'court-order':            '/icons/auction-gavel-judge-svgrepo-com.svg',
  'letters-testamentary':   '/icons/letters-svgrepo-com.svg',
  'death-certificate':      '/icons/death-certificate-svgrepo-com.svg',
  'w9':                     '/icons/tax-svgrepo-com.svg',
  'w8-ben':                 '/icons/tax-svgrepo-com.svg',
  'power-of-attorney':      '/icons/and-contract-document-svgrepo-com.svg',
} as const;

export type DocumentTypeSlug = keyof typeof DOCUMENT_TYPE_ICONS;

export function documentTypeIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase() as DocumentTypeSlug;
  return DOCUMENT_TYPE_ICONS[lower];
}

// ─── Captured Document Icons (v3 PDW Documents tab + Cart docs pane) ────────
// Bill 2026-05-13 — stylized document icons captured during onboarding.
// 10 stock illustrations (PNG) + 4 fresh AI-generated icons (SVG) covering
// the most-common document types FSRs capture. Additional gaps remain for
// future generation (court letters, agency letters, PCB-bespoke forms, etc.)
// — see icon-batch-summary.html for the full plan + remaining gaps.
//
// Naming convention: `doc-<slug>.png` or `doc-<slug>.svg` in
// apps/ROP-teams/public/icons/docs/. Mixed formats kept as-is — at icon
// display sizes (16–128 px) PNG vs SVG difference is invisible and
// vectorizing complex illustrations introduces artifacts.

export const DOCUMENT_ICONS = {
  // Personal IDs (single visa icon — class is metadata, not visual identity)
  'visa':                       '/icons/docs/doc-visa.svg',
  'us-passport':                '/icons/docs/doc-us-passport.svg',
  'foreign-passport':           '/icons/docs/doc-foreign-passport.svg',
  'drivers-license':            '/icons/docs/doc-drivers-license.svg',
  'ssn-card':                   '/icons/docs/doc-ssn-card.svg',
  'green-card':                 '/icons/docs/doc-green-card.svg',
  'birth-certificate':          '/icons/docs/doc-birth-certificate.svg',
  // Immigration support
  'ds2019':                     '/icons/docs/doc-ds2019.svg',
  'i20':                        '/icons/docs/doc-i20.svg',
  'i94':                        '/icons/docs/doc-i94.svg',
  'ead':                        '/icons/docs/doc-ead.svg',
  // Tax
  'tax-form':                   '/icons/docs/doc-tax-form.svg',
  // Trust & Estate
  'estate':                     '/icons/docs/doc-estate.svg',
  'trust-agreement':            '/icons/docs/doc-trust-agreement.svg',
  'death-certificate':          '/icons/docs/doc-death-certificate.svg',
  'vital-record':               '/icons/docs/doc-vital-record.svg',
  'probate-letters':            '/icons/docs/doc-probate-letters.svg',  // renamed from court-letters — Bill 2026-05-13
  // Business formation
  'articles-of-incorporation':  '/icons/docs/doc-articles-of-incorporation.svg',
  'articles-of-organization':   '/icons/docs/doc-articles-of-organization.svg',
  'partnership-agreement':      '/icons/docs/doc-partnership-agreement.svg',
  'operating-agreement':        '/icons/docs/doc-operating-agreement.svg',
  'corporate-bylaws':           '/icons/docs/doc-corporate-bylaws.svg',
  'dba-certificate':            '/icons/docs/doc-dba-certificate.svg',
  // Court / agency
  'court-order':                '/icons/docs/doc-court-order.svg',
  'fiduciary':                  '/icons/docs/doc-fiduciary.svg',
  'agency-letter':              '/icons/docs/doc-agency-letter.svg',
  // Special
  'ugma':                       '/icons/docs/doc-ugma.svg',
  // PCB-bespoke FSR forms
  'pcb-trust-certification':    '/icons/docs/pcb-trust-certification.svg',
  'pcb-banking-resolution':     '/icons/docs/pcb-banking-resolution.svg',
  'pcb-business-authorization': '/icons/docs/pcb-business-authorization.svg',
  'pcb-llc-certificate':        '/icons/docs/pcb-llc-certificate.svg',
  'pcb-poa-certification':      '/icons/docs/pcb-poa-certification.svg',
  'pcb-beneficial-ownership':   '/icons/docs/pcb-beneficial-ownership.svg',
  'pcb-pod-beneficiary':        '/icons/docs/pcb-pod-beneficiary.svg',
  'pcb-address-verification':   '/icons/docs/pcb-address-verification.svg',  // NEW — Bill 2026-05-13
} as const;

export type DocumentIconSlug = keyof typeof DOCUMENT_ICONS;

export function documentIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase() as DocumentIconSlug;
  return DOCUMENT_ICONS[lower];
}

// ─── Specific PCB Products ─────────────────────────────────────────────────
// Per-product icons within each category. Used by:
//   - Products picker tile screen (specific product tiles inside the
//     category-picker → product-tiles-picker flow per Bill 2026-05-12)
//   - AddToCartModal product header
//   - Cart card per-product badges
//
// Slugs are descriptive (kebab-case); align with actual Products table
// codes when wiring. Bill 2026-05-13 picks.

export const SPECIFIC_PRODUCT_ICONS = {
  // Checking (DDA variants)
  'free-checking':            '/icons/free-svgrepo-com.svg',
  'ultimate-checking':        '/icons/credit-card-money-svgrepo-com.svg',
  'student-checking':         '/icons/student-svgrepo-com.svg',
  'minor-student-checking':   '/icons/blocks-block-svgrepo-com.svg',

  // Savings (SAV variants)
  'minor-savings':            '/icons/piggy-bank-svgrepo-com.svg',
  'personal-savings':         '/icons/money-bag.svg',

  // Money Market (MMA variants)
  'premier-money-market':     '/icons/profits-dollar-svgrepo-com.svg',

  // CD (specific product, distinct from CD category icon)
  'cd-product':               '/icons/time-money-svgrepo-com.svg',

  // IRA (4 IRS-codified variants)
  'traditional-ira':          '/icons/traditional-ira.svg',
  'roth-ira':                 '/icons/roth-ira.svg',
  'sep-ira':                  '/icons/sep-ira.svg',
  'simple-ira':               '/icons/simple-ira.svg',
} as const;

export type SpecificProductSlug = keyof typeof SPECIFIC_PRODUCT_ICONS;

export function specificProductIconUrl(slug: string): string | undefined {
  const lower = slug.toLowerCase() as SpecificProductSlug;
  return SPECIFIC_PRODUCT_ICONS[lower];
}

// ─── Small Flat Vector Icons base path ─────────────────────────────────────
// Bill 2026-05-13 — designated this 118-icon set as the canonical source
// for inline UI affordances, replacing lucide-react across the codebase.
// Migration is phased; this constant + buildSmallFlatIconUrl() keep the
// URL-encoded path centralized so a future folder rename only touches here.

const SMALL_FLAT_BASE = '/Small%20Flat%20Vector%20Icons';

export function buildSmallFlatIconUrl(filename: string): string {
  return `${SMALL_FLAT_BASE}/${filename}`;
}

// ─── Entity-Bucket Icons (Bill 2026-05-13) ─────────────────────────────────
// 7 bucket SVGs rendered in EntityCategoryPicker's bucket grid (Person Add
// Wizard's first view). Keys match `EntityGroup.key` in entityGroups.ts so
// the picker resolves directly from the group definition. v0.14.42 wired
// the Special Purpose + Charitable buckets after the 7-bucket lock split.

export const ENTITY_BUCKET_ICONS = {
  personal:          '/icons/entity-buckets/entity-bucket-personal.png',  // Bill 2026-06-13 — clearer person icon (legible at tile size)
  business:          '/icons/entity-buckets/entity-bucket-business.svg',
  trust:             '/icons/entity-buckets/entity-bucket-trust-estate.svg',
  'special-purpose': '/icons/entity-buckets/entity-bucket-special-purpose.svg',
  fiduciary:         '/icons/entity-buckets/entity-bucket-fiduciary-managed.svg',
  charitable:        '/icons/entity-buckets/entity-bucket-charitable.svg',
  government:        '/icons/entity-buckets/entity-bucket-government-political.svg',
} as const;

export type EntityBucketKey = keyof typeof ENTITY_BUCKET_ICONS;

export function entityBucketIconUrl(key: string): string | undefined {
  return ENTITY_BUCKET_ICONS[key as EntityBucketKey];
}

// ─── Citizenship Status Tiles (Bill 2026-05-13) ────────────────────────────
// 3 PNG illustrations consumed by CitizenshipPicker — visual tile replacement
// for the legacy <select> in IndividualCitizenshipSection. Keys map to the
// `RaoCitizenshipStatus` string values stamped on cart peopleJson and sent
// to Core Director ('US Citizen' / 'Permanent Resident' / 'Non-Resident
// Alien'). PNG raster format — see docs/claude-design-icon-catalog/README.md
// for the re-export-to-SVG roadmap item.

export const CITIZENSHIP_ICONS = {
  'US Citizen':         '/icons/citizenship/citizenship-us-citizen.png',
  'Permanent Resident': '/icons/citizenship/citizenship-permanent-resident.png',
  'Non-Resident Alien': '/icons/citizenship/citizenship-non-permanent-resident.png',
} as const;

export type CitizenshipStatusSlug = keyof typeof CITIZENSHIP_ICONS;

export function citizenshipIconUrl(slug: string): string | undefined {
  return CITIZENSHIP_ICONS[slug as CitizenshipStatusSlug];
}

// ─── Status Tier Badges ────────────────────────────────────────────────────
// Per project_subtype_status_tiering_20260508.md — 24 v3 subtypes split
// across Production / UAT / Future. Future tier currently 5 subtypes
// (IOLTA, MedicalSetAside, USDAGrant, Donation, UnincorpAssoc).
//
// Production + UAT use no icon by default (text label sufficient). Future
// gets a distinct badge so picker surfaces visually flag "roadmap-only."

export const STATUS_TIER_ICONS = {
  Production: null,
  UAT:        null,
  Future:     '/icons/future.svg',
} as const;

export type StatusTier = keyof typeof STATUS_TIER_ICONS;

export function statusTierIconUrl(tier: string): string | null | undefined {
  return STATUS_TIER_ICONS[tier as StatusTier];
}
